/**
 * Dialog utility for Office Add-ins
 * Provides methods to show, manage and close dialogs
 */

// Declare Office global object
declare const Office: any;

// Dialog event types
export enum DialogEventType {
  DialogMessageReceived = 'DialogMessageReceived',
  DialogEventReceived = 'DialogEventReceived'
}

// Dialog options interface
export interface DialogOptionsDto {
  Width?: number;
  Height?: number;
  DisplayInIframe?: boolean;
}

export enum DialogErrorCode {
  NotFound = 12002,
  HttpsRequired = 12003,
  Closed = 12006
}

export enum EOfficeDialogMessageType {
  CloseDialog = "CLOSE_DIALOG",
  UpdateAttendees = "UPDATE_ATTENDEES",
  CommonDialogChosen = "COMMON_DIALOG_CHOSEN",
  ResourceSelected= "RESOURCE_SELECTED",
  SavedReservations = "SAVED_RESERVATIONS",
  UpdatedResourceSelected = "UPDATED_RESOURCE_SELECTED",
}

// Dialog model interface
export interface IDialogModel {
  addEventHandler: (eventType: DialogEventType, handler: (args: { message: string; origin: string | undefined } | { error: number }) => void) => void;
  close: () => void;
  messageChild: (message: string, messageOptions?: { targetOrigin: string }) => void;
}

/**
 * Dialog service class
 */
export class DialogService {
  private dialogObject: Office.Dialog | null = null;
  private eventHandlers: Map<string, Function[]> = new Map();
  private isDialogOpen: boolean = false;
  private lastUrl: string = '';
  
  /**
   * Check if a dialog is currently open
   */
  public get hasOpenDialog(): boolean {
    return this.isDialogOpen && this.dialogObject !== null;
  }

  /**
   * Show a dialog
   * @param url URL to open in dialog
   * @param options Dialog options
   * @param callback Callback that receives the dialog model
   */
  ShowDialog(url: string, options?: DialogOptionsDto, callback?: (dialog: IDialogModel) => void): void {
    // Check if dialog is already open
    if (this.hasOpenDialog) {
      console.warn('Another dialog is already open. Closing it before opening a new one.');
      this.CloseDialog();
    }
    
    // Cache the URL for potential reuse
    this.lastUrl = url;
    
    // Add cache-busting parameter to prevent caching issues
    if (!url.includes('t=')) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}t=${Date.now()}`;
    }

    // Setting up dialog options with defaults for better performance
    let dialogOptions: Office.DialogOptions = {
      displayInIframe: options?.DisplayInIframe !== undefined ? options.DisplayInIframe : true,
      width: options?.Width || 60,
      height: options?.Height || 40,
      promptBeforeOpen: false
    };

    try {
      // Display the dialog box using displayDialogAsync method
      Office.context.ui.displayDialogAsync(url, dialogOptions, (asyncResult: Office.AsyncResult<Office.Dialog>) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          this.isDialogOpen = true;
          
          // If the dialog box is successfully displayed, create a dialog model object
          const dialogObject = asyncResult.value;
          this.dialogObject = asyncResult.value;
          
          // Create a handler reference map for this dialog
          this.eventHandlers.clear();
          
          const dialogModel: IDialogModel = {
            // Method to add an event handler to the dialog box
            addEventHandler: (eventType: DialogEventType, handler: (args: { message: string; origin: string | undefined } | { error: number }) => void) => {
              const officeEventType = eventType === DialogEventType.DialogEventReceived ? Office.EventType.DialogEventReceived : Office.EventType.DialogMessageReceived;
              
              // Store reference to handler for cleanup
              if (!this.eventHandlers.has(officeEventType)) {
                this.eventHandlers.set(officeEventType, []);
              }
              this.eventHandlers.get(officeEventType)?.push(handler);
              
              dialogObject.addEventHandler(officeEventType, handler);
            },
            
            // Method to close the dialog box
            close: () => {
              try {
                dialogObject.close();
                this.isDialogOpen = false;
                this.dialogObject = null;
                this.eventHandlers.clear();
              } catch (error) {
                console.error('Error closing dialog:', error);
              }
            },
            
            // Method to send a message to the child window of the dialog box
            messageChild: (message: string, messageOptions?: { targetOrigin: string }) => {
              try {
                if (!messageOptions) {
                  dialogObject.messageChild(message);
                } else {
                  dialogObject.messageChild(message, messageOptions);
                }
              } catch (error) {
                console.error('Error sending message to dialog:', error);
              }
            },
          };
          
          // Call the callback function with the dialog model object
          if (callback) {
            try {
              callback(dialogModel);
            } catch (error) {
              console.error('Error in dialog callback:', error);
            }
          }
        } else {
          this.isDialogOpen = false;
          // If there's an error, log it to the console
          console.error("Failed to open dialog:", asyncResult.error);
        }
      });
    } catch (error) {
      console.error("Error opening dialog:", error);
      this.isDialogOpen = false;
    }
  }

  /**
   * Close the dialog
   */
  CloseDialog = () => {
    if (this.dialogObject) {
      try {
        this.dialogObject.close();
      } catch (error) {
        console.error('Error closing dialog:', error);
      } finally {
        this.dialogObject = null;
        this.isDialogOpen = false;
        this.eventHandlers.clear();
      }
    }
  };
  
  /**
   * Reopen the last dialog that was shown
   * @param options New options to use (optional)
   * @param callback Callback that receives the dialog model
   */
  ReopenLastDialog(options?: DialogOptionsDto, callback?: (dialog: IDialogModel) => void): void {
    if (this.lastUrl) {
      this.ShowDialog(this.lastUrl, options, callback);
    } else {
      console.warn('No previous dialog URL to reopen');
    }
  }
}

// Export singleton instance
export const dialogService = new DialogService();
